import { SlPopup } from '@shoelace-style/shoelace';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Disposable, disposeAll, onDOMEvent } from '../utils.js';
import { popoverCardStyles } from './cssstyles.js';

// The hide delay gives the pointer time to travel from the icon to the card.
const SHOW_DELAY_MS = 150;
const HIDE_DELAY_MS = 200;

/**
 * A question-mark icon that explains the UI next to it. Hovering or focusing the icon
 * shows a card with the slotted content, and clicking the icon pins the card open.
 * The card stays open while the pointer is over it, so it can hold links.
 *
 *   <fk-help href=${docsUrl('/api/fql/')}>
 *     <p>Filter tests with FQL.</p>
 *   </fk-help>
 */
@customElement('fk-help')
export class FKHelp extends LitElement {
  // A docs page, shown as a "Learn more" link at the bottom of the card.
  @property() href?: string;
  @property() placement: SlPopup['placement'] = 'top';

  @state() private _hovered = false;
  @state() private _focused = false;
  @state() private _pinned = false;

  @query('.trigger') private _trigger?: HTMLButtonElement;

  private _hoverTimer?: ReturnType<typeof setTimeout>;
  private _listeners: Disposable[] = [];

  constructor() {
    super();
    this.addEventListener('pointerenter', event => this._onPointerEnter(event));
    this.addEventListener('pointerleave', event => this._onPointerLeave(event));
    this.addEventListener('focusout', event => this._onFocusOut(event));
    this.addEventListener('click', event => this._onClick(event));
  }

  override connectedCallback() {
    super.connectedCallback();
    this._listeners = [
      onDOMEvent(document, 'pointerdown', event => {
        if (this._pinned && !event.composedPath().includes(this))
          this._pinned = false;
      }),
      onDOMEvent(document, 'keydown', event => {
        if (event.key === 'Escape')
          this._close();
      }),
    ];
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    disposeAll(this._listeners);
    clearTimeout(this._hoverTimer);
  }

  private _close() {
    clearTimeout(this._hoverTimer);
    this._hovered = false;
    this._focused = false;
    this._pinned = false;
  }

  // Touch has no hover: there, the card opens on tap only.
  private _onPointerEnter(event: PointerEvent) {
    if (event.pointerType !== 'mouse')
      return;
    clearTimeout(this._hoverTimer);
    if (!this._hovered)
      this._hoverTimer = setTimeout(() => this._hovered = true, SHOW_DELAY_MS);
  }

  private _onPointerLeave(event: PointerEvent) {
    if (event.pointerType !== 'mouse')
      return;
    clearTimeout(this._hoverTimer);
    if (this._hovered)
      this._hoverTimer = setTimeout(() => this._hovered = false, HIDE_DELAY_MS);
  }

  private _onFocus() {
    // Mouse clicks focus the button too; only keyboard focus should open the card.
    if (this._trigger?.matches(':focus-visible'))
      this._focused = true;
  }

  private _onFocusOut(event: FocusEvent) {
    const next = event.relatedTarget as Node | null;
    if (next && (this.contains(next) || this.renderRoot.contains(next)))
      return;
    this._focused = false;
  }

  // The help icon may sit inside a link, like the report outcome chips.
  // A click anywhere in the icon or the card must not reach that link, and
  // links inside the card must keep working.
  private _onClick(event: MouseEvent) {
    const path = event.composedPath();
    const link = path.slice(0, path.indexOf(this)).find(node => node instanceof HTMLAnchorElement && node.hasAttribute('href')) as HTMLAnchorElement | undefined;
    if (link) {
      // The router handles same-tab links; it would resolve other links to the outer link.
      if (link.target.startsWith('_') || link.hasAttribute('download'))
        event.stopPropagation();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (this._trigger && path.includes(this._trigger))
      this._pinned = !this._pinned;
  }

  override render() {
    const open = this._hovered || this._focused || this._pinned;
    return html`
      <sl-popup
        strategy=fixed
        placement=${this.placement}
        distance=6
        flip
        shift
        shift-padding=8
        ?active=${open}
      >
        <button
          slot=anchor
          class=trigger
          type=button
          aria-label=Help
          aria-expanded=${open}
          aria-controls=card
          @focus=${this._onFocus}
        ><sl-icon name=question-circle></sl-icon></button>
        <div id=card class="card popover-card">
          <slot></slot>
          ${this.href ? html`
            <a class=learn-more href=${this.href} target=_blank rel=noopener>
              Learn more <sl-icon library=boxicons name=bx-link-external></sl-icon>
            </a>
          ` : nothing}
        </div>
      </sl-popup>
    `;
  }

  static styles = [popoverCardStyles, css`
    :host {
      display: inline-flex;
      vertical-align: middle;
    }

    .trigger {
      display: flex;
      align-items: center;
      padding: 0;
      margin: 0;
      border: none;
      border-radius: 50%;
      background: none;
      font-size: inherit;
      color: var(--sl-color-neutral-500);
      cursor: help;

      &:hover {
        color: var(--sl-color-neutral-700);
      }

      &:focus-visible {
        outline: var(--sl-focus-ring);
        outline-offset: 2px;
      }
    }

    sl-popup::part(popup) {
      z-index: var(--sl-z-index-tooltip);
    }

    /* The card resets everything it could inherit from the place it's used in. */
    .card {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-x-small);
      width: max-content;
      max-width: min(20rem, calc(100vw - 16px));
      padding: var(--sl-spacing-small) var(--sl-spacing-medium);
      color: var(--sl-color-neutral-700);
      font-family: var(--sl-font-sans);
      font-size: var(--sl-font-size-small);
      font-weight: var(--sl-font-weight-normal);
      font-style: normal;
      line-height: var(--sl-line-height-normal);
      letter-spacing: normal;
      text-align: start;
      text-transform: none;
      white-space: normal;
      cursor: auto;
    }

    ::slotted(*) {
      margin: 0;
    }

    .learn-more {
      align-self: flex-start;
      display: inline-flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      color: var(--sl-color-primary-600);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  `];
}
